export type SourceClass = 'organic' | 'direct' | 'social' | 'email' | 'referral';

export interface UtmParams {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
}

export interface Attribution {
  source: SourceClass;
  utm: UtmParams;
  referrer: string;
  capturedAt: string;
}

const STORAGE_KEY = 'cfpl_attribution';
const ORGANIC_HOSTS = ['google.', 'bing.com', 'duckduckgo.com', 'yandex.', 'search.yahoo.com'];
const SOCIAL_HOSTS = ['twitter.com', 'x.com', 't.me', 'facebook.com', 'reddit.com', 'instagram.com', 'youtube.com'];

export function parseUtm(params: URLSearchParams): UtmParams {
  const get = (k: string) => params.get(k) ?? undefined;
  const utm: UtmParams = {
    source: get('utm_source'),
    medium: get('utm_medium'),
    campaign: get('utm_campaign'),
    term: get('utm_term'),
    content: get('utm_content'),
  };
  return Object.values(utm).some(Boolean) ? utm : {};
}

export function classifySource(ctx: { referrer: string; utm: UtmParams }): SourceClass {
  const { referrer, utm } = ctx;
  if (utm.campaign === 'retention' || utm.medium === 'email') return 'email';
  if (utm.medium === 'organic') return 'organic';
  if (utm.medium === 'social') return 'social';
  if (referrer) {
    let host = '';
    try { host = new URL(referrer).hostname.toLowerCase(); } catch { return 'referral'; }
    if (ORGANIC_HOSTS.some((h) => host.includes(h))) return 'organic';
    if (SOCIAL_HOSTS.some((h) => host.includes(h))) return 'social';
    return 'referral';
  }
  return 'direct';
}

export function captureSessionAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const utm = parseUtm(params);
  // Same-origin referrer = internal navigation, not an acquisition source
  let referrer = document.referrer || '';
  try {
    if (referrer && new URL(referrer).origin === window.location.origin) referrer = '';
  } catch { /* keep as-is */ }
  const hasUtm = Object.values(utm).some(Boolean);
  // Capture (or refresh) only when UTM present OR no stored attribution yet
  const stored = readAttribution();
  if (!hasUtm && !referrer && stored) return stored;
  const attribution: Attribution = {
    source: classifySource({ referrer, utm }),
    utm,
    referrer,
    capturedAt: new Date().toISOString(),
  };
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution)); } catch { /* ignore */ }
  return attribution;
}

export function readAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch { return null; }
}

export function getAttributionSource(): SourceClass {
  return readAttribution()?.source ?? 'direct';
}
